/**
 * Route-template matching for URL sanitisation. Reduces a concrete URL path to
 * the registered route template that produced it, so telemetry carries a
 * template (`/members/:id`) rather than an interpolated path (ADR-069 Rule 1).
 *
 * Matching is by template, never by identifier *shape*. A shape rule cannot be
 * the safety mechanism here: identifiers in this estate are not reliably
 * UUID-shaped (backend2 `identityId` is an unvalidated `string`, and sibling
 * path positions are numeric), so a short value like `member-123` slips through
 * any conservative shape heuristic. A registered template knows which segments
 * are identifiers regardless of what they contain.
 *
 * Two wildcard syntaxes are accepted because the registries use different ones:
 * `{param}` (the OpenAPI manifest) and `:param` (client route paths).
 */

/** Prepared lookup. Build once per template set; matching allocates nothing per call. */
export interface RouteTemplateIndex {
  /** Wildcard-free templates, keyed by their exact path. */
  readonly exact: ReadonlyMap<string, string>;
  /** Templates carrying at least one wildcard, bucketed by segment count. */
  readonly bySegmentCount: ReadonlyMap<number, readonly CompiledTemplate[]>;
}

interface CompiledTemplate {
  /** The template as authored, returned verbatim on a match. */
  readonly template: string;
  /** One entry per path segment; `null` means "wildcard, matches any single segment". */
  readonly segments: readonly (string | null)[];
  readonly wildcardCount: number;
}

function isWildcard(segment: string): boolean {
  return (segment.startsWith('{') && segment.endsWith('}')) || segment.startsWith(':');
}

/**
 * Normalise a path for matching: guarantee a leading slash and drop a trailing
 * one (except at the root). The leading-slash guarantee matters because clients
 * may author a path without it — `scheduling/appointments/me` is resolved by
 * axios against the configured `baseURL`, and without normalisation it would
 * never match its own registered template.
 */
export function normalisePath(path: string): string {
  let p = path.startsWith('/') ? path : `/${path}`;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

export function compileRouteTemplates(templates: readonly string[]): RouteTemplateIndex {
  const exact = new Map<string, string>();
  const buckets = new Map<number, CompiledTemplate[]>();

  for (const raw of templates) {
    if (!raw) continue;
    const template = normalisePath(raw);
    const parts = template.split('/');
    const segments = parts.map((s) => (isWildcard(s) ? null : s));
    const wildcardCount = segments.filter((s) => s === null).length;

    if (wildcardCount === 0) {
      // First writer wins so a duplicate entry can't change the resolved value.
      if (!exact.has(template)) exact.set(template, template);
      continue;
    }
    const bucket = buckets.get(segments.length);
    if (bucket) bucket.push({ template, segments, wildcardCount });
    else buckets.set(segments.length, [{ template, segments, wildcardCount }]);
  }

  // Sort each bucket once, so matching can take the first survivor rather than
  // scanning for a minimum. Fewest wildcards first (most specific), then
  // lexicographic — a total order, so a tie can never resolve non-deterministically.
  // `forEach` rather than iterating `.values()`: this module is compiled directly
  // by consumer apps whose tsconfig target predates map iterators.
  buckets.forEach((bucket: CompiledTemplate[]) => {
    bucket.sort(
      (a: CompiledTemplate, b: CompiledTemplate) =>
        a.wildcardCount - b.wildcardCount ||
        (a.template < b.template ? -1 : a.template > b.template ? 1 : 0),
    );
  });

  return { exact, bySegmentCount: buckets };
}

/**
 * Resolve a concrete path to its template, or `null` when nothing matches.
 *
 * Returning `null` rather than a best guess is the point: the caller treats an
 * unmatched path as fail-closed and destroys it. A heuristic fallback here would
 * silently convert "unknown" into "looks fine".
 */
export function matchRouteTemplate(index: RouteTemplateIndex, path: string): string | null {
  const normalised = normalisePath(path);

  // A wildcard-free template that equals the path wins outright. Without this,
  // a static route sharing a shape with a templated one (`/api/v1/check-ins/batch`
  // vs `/api/v1/check-ins/{date}`) would be reported under the wildcard.
  const literal = index.exact.get(normalised);
  if (literal !== undefined) return literal;

  const segments = normalised.split('/');
  const candidates = index.bySegmentCount.get(segments.length);
  if (!candidates) return null;

  for (const candidate of candidates) {
    let matched = true;
    for (let i = 0; i < segments.length; i += 1) {
      const expected = candidate.segments[i];
      if (expected !== null && expected !== segments[i]) {
        matched = false;
        break;
      }
    }
    // Buckets are pre-sorted most-specific-first, so the first survivor is the answer.
    if (matched) return candidate.template;
  }
  return null;
}
